/**
 * Pure web-mercator helpers.
 *
 * These mirror `mapboxgl.MercatorCoordinate` exactly — the same formulas
 * `CoordinateService` relies on — but without importing `mapbox-gl`, so the
 * import engine (and its tests) stay free of browser globals.
 *
 * Equivalence with `CoordinateService.lngLatToScenePosition` /
 * `scenePositionToLngLat` is asserted in `__tests__/transform.test.ts`.
 */

import { FEET_TO_METERS, METERS_TO_FEET } from '@/constants/units'

/** mapbox-gl: `const earthRadius = 6371008.8` */
const EARTH_RADIUS = 6371008.8
const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS

function circumferenceAtLatitude(lat: number): number {
  return EARTH_CIRCUMFERENCE * Math.cos((lat * Math.PI) / 180)
}

export interface MercatorPoint {
  x: number
  y: number
}

export function mercatorFromLngLat(lng: number, lat: number): MercatorPoint {
  return {
    x: (180 + lng) / 360,
    y:
      (180 -
        (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) /
      360,
  }
}

export function lngLatFromMercator(x: number, y: number): { lng: number; lat: number } {
  return {
    lng: x * 360 - 180,
    lat: (360 / Math.PI) * Math.atan(Math.exp(((180 - y * 360) * Math.PI) / 180)) - 90,
  }
}

/** Mercator units per metre at the given latitude. */
export function meterInMercatorUnits(lat: number): number {
  return 1 / circumferenceAtLatitude(lat)
}

/**
 * Offset of `target` from `center`, in FEET, in PlaneMAX scene axes
 * (+X east, +Z south).
 *
 * Matches `CoordinateService.lngLatToScenePosition`, including its choice to
 * take the metre scale at the TARGET latitude.
 */
export function lngLatToSceneFeet(
  target: { lat: number; lng: number },
  center: { lat: number; lng: number }
): [number, number, number] {
  const t = mercatorFromLngLat(target.lng, target.lat)
  const c = mercatorFromLngLat(center.lng, center.lat)
  const scale = meterInMercatorUnits(target.lat)

  const metersX = (t.x - c.x) / scale
  const metersZ = (t.y - c.y) / scale // Mercator +Y is south, like scene +Z

  return [metersX * METERS_TO_FEET, 0, metersZ * METERS_TO_FEET]
}

/**
 * Inverse of {@link lngLatToSceneFeet}.
 *
 * Matches `CoordinateService.scenePositionToLngLat`, including its choice to
 * take the metre scale at the CENTER latitude.
 */
export function sceneFeetToLngLat(
  position: [number, number, number],
  center: { lat: number; lng: number }
): { lat: number; lng: number } {
  const c = mercatorFromLngLat(center.lng, center.lat)
  const scale = meterInMercatorUnits(center.lat)

  const x = c.x + position[0] * FEET_TO_METERS * scale
  const y = c.y + position[2] * FEET_TO_METERS * scale

  return lngLatFromMercator(x, y)
}
